// =====================================================
// DOCUMENT LAYOUT — what the on-screen preview needs to look like Word.
//
// The parser keeps each paragraph's own pPr/rPr XML, but Word's look also comes from
// styles.xml (paragraph/character/table styles and docDefaults), the theme's fonts,
// numbering.xml (auto-numbers like "1." that are not in the text), settings.xml (the
// default tab stop) and the section's page size and margins. buildDocLayout reads
// those parts once per document and resolves effective paragraph/run/table formatting
// for the renderer.
//
// PREVIEW ONLY: nothing here touches a RichParagraph or the DOCX generator. The layout
// is attached to the parsed paragraphs through a WeakMap, so paragraphs that never got
// one (PDF, text, RTF) simply render with their direct formatting as before.
// =====================================================

import type { RichParagraph } from '../types';

export interface ParaFmt {
  jc?: string;
  indL?: number;        // twips
  indR?: number;
  indFirst?: number;    // twips: + first-line indent, - hanging
  spBefore?: number;    // twips
  spAfter?: number;
  line?: number;        // 240ths of a line (auto) or twips (exact/atLeast)
  lineRule?: string;
  contextual?: boolean;
  numId?: string;
  ilvl?: number;
  shd?: string;         // background hex
  bdr?: Partial<Record<'top' | 'left' | 'bottom' | 'right', Border>>;
}

export interface RunFmt {
  font?: string;
  sz?: number;          // half-points
  b?: boolean;
  i?: boolean;
  u?: boolean;
  strike?: boolean;
  dstrike?: boolean;
  caps?: boolean;
  smallCaps?: boolean;
  vanish?: boolean;
  color?: string;
  highlight?: string;   // CSS color
  shd?: string;
  vertAlign?: string;
}

export interface Border { style: string; widthPt: number; color: string; spacePt: number }

export interface NumLabel { text: string; suff: string; run: RunFmt }

export interface TableFmt {
  gridTw: number[];
  indTw: number;
  jc?: string;
  cellMar: { left: number; right: number; top: number; bottom: number };
  borders: Partial<Record<'top' | 'left' | 'bottom' | 'right' | 'insideH' | 'insideV', Border>>;
  styleId?: string;
}

export interface CellFmt {
  widthTw?: number;
  span: number;
  vMerge?: 'restart' | 'continue';
  vAlign?: string;
  shd?: string;
  borders: Partial<Record<'top' | 'left' | 'bottom' | 'right', Border>>;
}

interface StyleDef {
  type: string;
  basedOn?: string;
  pPr: string;
  rPr: string;
  tblPr: string;
  numId?: string;
}

interface LvlDef { start: number; numFmt: string; lvlText: string; suff: string; restart?: number; isLgl: boolean; pPr: string; rPr: string }

export interface DocLayout {
  styles: Map<string, StyleDef>;
  defaultParaStyle?: string;
  defaultTableStyle?: string;
  defaultPPr: string;
  defaultRPr: string;
  theme: { major?: string; minor?: string };
  abstracts: Map<string, Map<number, LvlDef>>;
  nums: Map<string, { abstractId: string; overrides: Map<number, { start?: number; lvl?: LvlDef }> }>;
  pageWidthTw: number;
  marginLTw: number;
  marginRTw: number;
  defaultTabTw: number;
  labels: WeakMap<RichParagraph, NumLabel>;
  rowPos: WeakMap<RichParagraph, { first: boolean; last: boolean }>;
  tableStyleOf: WeakMap<RichParagraph, string>;
  paraCache: Map<string, { para: ParaFmt; run: RunFmt }>;
  runCache: Map<string, RunFmt>;
}

const layoutOfPara = new WeakMap<RichParagraph, DocLayout>();

/** The layout a parsed paragraph (or table row / cell paragraph) belongs to, if any. */
export function layoutOf(rich: RichParagraph | undefined): DocLayout | undefined {
  return rich ? layoutOfPara.get(rich) : undefined;
}

// ---------- small XML helpers (regex over serialized fragments, like rPrToCss) ----------

function block(xml: string, tag: string): string {
  const m = xml.match(new RegExp(`<w:${tag}(?=[\\s>/])[^>]*?(?:/>|>([\\s\\S]*?)</w:${tag}>)`));
  return m ? m[0] : '';
}

function attrs(xml: string, tag: string): Record<string, string> | null {
  const m = xml.match(new RegExp(`<w:${tag}(?=[\\s/>])([^>]*)>`));
  if (!m) return null;
  const out: Record<string, string> = {};
  for (const a of m[1].matchAll(/(?:w:)?(\w+)="([^"]*)"/g)) out[a[1]] = a[2];
  return out;
}

function onOff(xml: string, tag: string): boolean | undefined {
  const a = attrs(xml, tag);
  if (!a) return undefined;
  return a.val === undefined || !/^(0|false|off)$/i.test(a.val);
}

function num(v: string | undefined): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** Paragraph properties without the paragraph-mark rPr, tracked-change snapshot or sectPr. */
function cleanPPr(pPr: string): string {
  return pPr
    .replace(/<w:pPrChange\b[\s\S]*?<\/w:pPrChange>/g, '')
    .replace(/<w:rPr\b[^>]*\/>|<w:rPr\b[\s\S]*?<\/w:rPr>/g, '')
    .replace(/<w:sectPr\b[\s\S]*?<\/w:sectPr>/g, '');
}

function cleanRPr(rPr: string): string {
  return rPr.replace(/<w:rPrChange\b[\s\S]*?<\/w:rPrChange>/g, '');
}

const HIGHLIGHT: Record<string, string> = {
  yellow: '#FFFF00', green: '#00FF00', cyan: '#00FFFF', magenta: '#FF00FF', blue: '#0000FF',
  red: '#FF0000', darkBlue: '#000080', darkCyan: '#008080', darkGreen: '#008000',
  darkMagenta: '#800080', darkRed: '#800000', darkYellow: '#808000', darkGray: '#808080',
  lightGray: '#C0C0C0', black: '#000000', white: '#FFFFFF',
};

function shdFill(xml: string, tag = 'shd'): string | undefined {
  const a = attrs(xml, tag);
  if (!a) return undefined;
  if (a.fill && a.fill !== 'auto' && /^[0-9A-Fa-f]{6}$/.test(a.fill)) return '#' + a.fill;
  // Solid pattern shading uses the pattern colour.
  if (a.val === 'solid' && a.color && /^[0-9A-Fa-f]{6}$/.test(a.color)) return '#' + a.color;
  return undefined;
}

function parseBorder(xml: string, tag: string): Border | undefined {
  const a = attrs(xml, tag);
  if (!a) return undefined;
  const v = a.val || 'none';
  if (v === 'none' || v === 'nil') return { style: 'none', widthPt: 0, color: '', spacePt: 0 };
  const style = v === 'double' ? 'double' : /dot/.test(v) ? 'dotted' : /dash/.test(v) ? 'dashed' : 'solid';
  let widthPt = (num(a.sz) ?? 4) / 8;
  if (style === 'double') widthPt = Math.max(widthPt * 3, 2.25);
  const color = a.color && a.color !== 'auto' && /^[0-9A-Fa-f]{6}$/.test(a.color) ? '#' + a.color : '#000000';
  return { style, widthPt: Math.max(widthPt, 0.5), color, spacePt: num(a.space) ?? 0 };
}

function parseBorders<K extends string>(xml: string, container: string, sides: K[]): Partial<Record<K, Border>> {
  const out: Partial<Record<K, Border>> = {};
  const b = block(xml, container);
  if (!b) return out;
  for (const s of sides) {
    // OOXML 2010+ also writes start/end for left/right.
    const alt = s === 'left' ? 'start' : s === 'right' ? 'end' : '';
    const bd = parseBorder(b, s) || (alt ? parseBorder(b, alt) : undefined);
    if (bd) out[s] = bd;
  }
  return out;
}

// ---------- property parsing ----------

function parsePara(pPrRaw: string): ParaFmt {
  const pPr = cleanPPr(pPrRaw);
  const f: ParaFmt = {};
  const jc = attrs(pPr, 'jc');
  if (jc?.val) f.jc = jc.val;
  const ind = attrs(pPr, 'ind');
  if (ind) {
    const l = num(ind.left ?? ind.start);
    const r = num(ind.right ?? ind.end);
    if (l !== undefined) f.indL = l;
    if (r !== undefined) f.indR = r;
    if (ind.hanging !== undefined) f.indFirst = -(num(ind.hanging) ?? 0);
    else if (ind.firstLine !== undefined) f.indFirst = num(ind.firstLine) ?? 0;
  }
  const sp = attrs(pPr, 'spacing');
  if (sp) {
    if (sp.before !== undefined) f.spBefore = num(sp.before);
    if (sp.after !== undefined) f.spAfter = num(sp.after);
    if (sp.beforeAutospacing && /^(1|true|on)$/.test(sp.beforeAutospacing)) f.spBefore = 280;
    if (sp.afterAutospacing && /^(1|true|on)$/.test(sp.afterAutospacing)) f.spAfter = 280;
    if (sp.line !== undefined) { f.line = num(sp.line); f.lineRule = sp.lineRule || 'auto'; }
  }
  const ctx = onOff(pPr, 'contextualSpacing');
  if (ctx !== undefined) f.contextual = ctx;
  const numPr = block(pPr, 'numPr');
  if (numPr) {
    const id = attrs(numPr, 'numId')?.val;
    const lvl = num(attrs(numPr, 'ilvl')?.val);
    if (id !== undefined) f.numId = id;
    if (lvl !== undefined) f.ilvl = lvl;
  }
  const shd = shdFill(pPr);
  if (shd) f.shd = shd;
  const bdr = parseBorders(pPr, 'pBdr', ['top', 'left', 'bottom', 'right'] as const);
  if (Object.keys(bdr).length) f.bdr = bdr;
  return f;
}

function parseRun(rPrRaw: string, theme: DocLayout['theme']): RunFmt {
  if (!rPrRaw) return {};
  const rPr = cleanRPr(rPrRaw);
  const f: RunFmt = {};
  const fonts = attrs(rPr, 'rFonts');
  if (fonts) {
    if (fonts.ascii) f.font = fonts.ascii;
    else if (fonts.asciiTheme) f.font = /^major/.test(fonts.asciiTheme) ? theme.major : theme.minor;
    else if (fonts.hAnsi) f.font = fonts.hAnsi;
    else if (fonts.hAnsiTheme) f.font = /^major/.test(fonts.hAnsiTheme) ? theme.major : theme.minor;
  }
  const sz = num(attrs(rPr, 'sz')?.val);
  if (sz !== undefined) f.sz = sz;
  const toggles: [keyof RunFmt, string][] = [
    ['b', 'b'], ['i', 'i'], ['strike', 'strike'], ['dstrike', 'dstrike'],
    ['caps', 'caps'], ['smallCaps', 'smallCaps'], ['vanish', 'vanish'],
  ];
  for (const [k, tag] of toggles) {
    const v = onOff(rPr, tag);
    if (v !== undefined) (f as any)[k] = v;
  }
  const u = attrs(rPr, 'u');
  if (u) f.u = !!u.val && u.val !== 'none';
  const color = attrs(rPr, 'color')?.val;
  if (color) f.color = color === 'auto' ? 'auto' : '#' + color;
  const hl = attrs(rPr, 'highlight')?.val;
  if (hl) f.highlight = hl === 'none' ? 'none' : (HIGHLIGHT[hl] || 'none');
  const shd = shdFill(rPr);
  if (shd) f.shd = shd;
  const va = attrs(rPr, 'vertAlign')?.val;
  if (va) f.vertAlign = va;
  return f;
}

function mergeDefined<T extends object>(...parts: (T | undefined)[]): T {
  const out: any = {};
  for (const p of parts) {
    if (!p) continue;
    for (const k of Object.keys(p)) {
      const v = (p as any)[k];
      if (v !== undefined) out[k] = k === 'bdr' && out.bdr ? { ...out.bdr, ...v } : v;
    }
  }
  return out;
}

// ---------- building ----------

function parseStyles(stylesXml: string): Pick<DocLayout, 'styles' | 'defaultParaStyle' | 'defaultTableStyle' | 'defaultPPr' | 'defaultRPr'> {
  const styles = new Map<string, StyleDef>();
  let defaultParaStyle: string | undefined;
  let defaultTableStyle: string | undefined;
  const dd = block(stylesXml, 'docDefaults');
  const defaultPPr = block(block(dd, 'pPrDefault'), 'pPr');
  const defaultRPr = block(block(dd, 'rPrDefault'), 'rPr');
  for (const m of stylesXml.matchAll(/<w:style\b([^>]*)>([\s\S]*?)<\/w:style>/g)) {
    const head = m[1];
    const id = head.match(/w:styleId="([^"]+)"/)?.[1];
    if (!id) continue;
    const type = head.match(/w:type="([^"]+)"/)?.[1] || 'paragraph';
    // Conditional table formatting (first row, banding) carries its own pPr/rPr.
    const body = m[2].replace(/<w:tblStylePr\b[\s\S]*?<\/w:tblStylePr>/g, '');
    const pPr = block(body, 'pPr');
    const def: StyleDef = {
      type,
      basedOn: attrs(body, 'basedOn')?.val,
      pPr,
      rPr: block(body, 'rPr'),
      tblPr: block(body, 'tblPr'),
    };
    styles.set(id, def);
    if (/w:default="(1|true)"/.test(head)) {
      if (type === 'paragraph') defaultParaStyle = id;
      else if (type === 'table') defaultTableStyle = id;
    }
  }
  return { styles, defaultParaStyle, defaultTableStyle, defaultPPr, defaultRPr };
}

function parseLvl(xml: string): LvlDef {
  return {
    start: num(attrs(xml, 'start')?.val) ?? 0,
    numFmt: attrs(xml, 'numFmt')?.val || 'decimal',
    lvlText: attrs(xml, 'lvlText')?.val ?? '',
    suff: attrs(xml, 'suff')?.val || 'tab',
    restart: num(attrs(xml, 'lvlRestart')?.val),
    isLgl: onOff(xml, 'isLgl') === true,
    pPr: block(xml, 'pPr'),
    rPr: block(xml, 'rPr'),
  };
}

function parseNumbering(xml: string): Pick<DocLayout, 'abstracts' | 'nums'> {
  const abstracts = new Map<string, Map<number, LvlDef>>();
  const nums: DocLayout['nums'] = new Map();
  for (const m of xml.matchAll(/<w:abstractNum\b[^>]*w:abstractNumId="([^"]+)"[^>]*>([\s\S]*?)<\/w:abstractNum>/g)) {
    const lvls = new Map<number, LvlDef>();
    for (const l of m[2].matchAll(/<w:lvl\b[^>]*w:ilvl="(\d+)"[^>]*>([\s\S]*?)<\/w:lvl>/g)) {
      lvls.set(Number(l[1]), parseLvl(l[2]));
    }
    abstracts.set(m[1], lvls);
  }
  for (const m of xml.matchAll(/<w:num\b[^>]*w:numId="([^"]+)"[^>]*>([\s\S]*?)<\/w:num>/g)) {
    const abstractId = attrs(m[2], 'abstractNumId')?.val;
    if (abstractId === undefined) continue;
    const overrides = new Map<number, { start?: number; lvl?: LvlDef }>();
    for (const o of m[2].matchAll(/<w:lvlOverride\b[^>]*w:ilvl="(\d+)"[^>]*>([\s\S]*?)<\/w:lvlOverride>/g)) {
      const lvlXml = o[2].match(/<w:lvl\b[\s\S]*?<\/w:lvl>/)?.[0];
      overrides.set(Number(o[1]), {
        start: num(attrs(o[2], 'startOverride')?.val),
        lvl: lvlXml ? parseLvl(lvlXml) : undefined,
      });
    }
    nums.set(m[1], { abstractId, overrides });
  }
  return { abstracts, nums };
}

/** Read the parts the preview needs and attach the layout to every parsed paragraph. */
export async function buildDocLayout(zip: any, docXml: string, richParagraphs: RichParagraph[]): Promise<DocLayout> {
  const read = async (name: string): Promise<string> => {
    const f = zip.file(name);
    return f ? await f.async('string') : '';
  };
  const [stylesXml, numberingXml, themeXml, settingsXml] = await Promise.all([
    read('word/styles.xml'), read('word/numbering.xml'), read('word/theme/theme1.xml'), read('word/settings.xml'),
  ]);

  const theme: DocLayout['theme'] = {
    major: themeXml.match(/<a:majorFont>[\s\S]*?<a:latin typeface="([^"]*)"/)?.[1] || undefined,
    minor: themeXml.match(/<a:minorFont>[\s\S]*?<a:latin typeface="([^"]*)"/)?.[1] || undefined,
  };

  // The body's own section (the last sectPr) sets the page the text is laid out on.
  const sects = docXml.match(/<w:sectPr\b[\s\S]*?<\/w:sectPr>/g);
  const sect = sects ? sects[sects.length - 1] : '';
  const pgSz = attrs(sect, 'pgSz');
  const pgMar = attrs(sect, 'pgMar');

  const layout: DocLayout = {
    ...parseStyles(stylesXml),
    ...parseNumbering(numberingXml),
    theme,
    pageWidthTw: num(pgSz?.w) ?? 12240,
    marginLTw: num(pgMar?.left) ?? 1440,
    marginRTw: num(pgMar?.right) ?? 1440,
    defaultTabTw: num(attrs(settingsXml, 'defaultTabStop')?.val) || 720,
    labels: new WeakMap(),
    rowPos: new WeakMap(),
    tableStyleOf: new WeakMap(),
    paraCache: new Map(),
    runCache: new Map(),
  };

  // Attach, and number list paragraphs in document order (table cells included).
  const counters = new Map<string, number[]>();    // per abstractNum, per level
  const seenNum = new Set<string>();
  const visit = (p: RichParagraph) => {
    layoutOfPara.set(p, layout);
    const label = nextLabel(layout, p, counters, seenNum);
    if (label) layout.labels.set(p, label);
  };
  const sameTable = (a?: RichParagraph, b?: RichParagraph) =>
    !!a && !!b && !!a.isTableRow && !!b.isTableRow && a.tablePropsXml === b.tablePropsXml;
  for (let i = 0; i < richParagraphs.length; i++) {
    const p = richParagraphs[i];
    layoutOfPara.set(p, layout);
    if (p.isTableRow && p.cellData) {
      layout.rowPos.set(p, { first: !sameTable(richParagraphs[i - 1], p), last: !sameTable(p, richParagraphs[i + 1]) });
      const tblStyle = attrs(p.tablePropsXml || '', 'tblStyle')?.val || layout.defaultTableStyle;
      for (const cell of p.cellData.cells) {
        for (const cp of cell.paragraphs) {
          if (tblStyle) layout.tableStyleOf.set(cp, tblStyle);
          visit(cp);
        }
      }
    } else if (!p.isPassthrough) {
      visit(p);
    }
  }
  return layout;
}

// ---------- style resolution ----------

function styleChain(layout: DocLayout, id: string | undefined): StyleDef[] {
  const chain: StyleDef[] = [];
  const seen = new Set<string>();
  while (id && !seen.has(id)) {
    seen.add(id);
    const s = layout.styles.get(id);
    if (!s) break;
    chain.unshift(s);
    id = s.basedOn;
  }
  return chain;
}

function pStyleOf(layout: DocLayout, pPrXml: string): string | undefined {
  return attrs(pPrXml, 'pStyle')?.val || layout.defaultParaStyle;
}

/** Effective paragraph formatting and the paragraph's base run formatting (style level). */
export function resolvePara(rich: RichParagraph): { para: ParaFmt; run: RunFmt; layout?: DocLayout } {
  const layout = layoutOf(rich);
  if (!layout) return { para: parsePara(rich.pPrXml || ''), run: {} };
  const tblStyle = layout.tableStyleOf.get(rich) || '';
  const key = tblStyle + '\u0000' + (rich.pPrXml || '');
  let hit = layout.paraCache.get(key);
  if (!hit) {
    const chain = styleChain(layout, pStyleOf(layout, rich.pPrXml || ''));
    const tblChain = tblStyle ? styleChain(layout, tblStyle) : [];
    const direct = parsePara(rich.pPrXml || '');
    // Numbering's indents sit between the style and direct formatting.
    const styled = mergeDefined<ParaFmt>(
      parsePara(layout.defaultPPr), ...tblChain.map(s => parsePara(s.pPr)), ...chain.map(s => parsePara(s.pPr)));
    const numId = direct.numId ?? styled.numId;
    const ilvl = direct.ilvl ?? styled.ilvl ?? 0;
    const lvl = numId && numId !== '0' ? lvlDef(layout, numId, ilvl) : undefined;
    const para = mergeDefined<ParaFmt>(styled, lvl ? parsePara(lvl.pPr) : undefined, direct);
    const run = mergeDefined<RunFmt>(
      parseRun(layout.defaultRPr, layout.theme), ...tblChain.map(s => parseRun(s.rPr, layout.theme)),
      ...chain.map(s => parseRun(s.rPr, layout.theme)));
    hit = { para, run };
    layout.paraCache.set(key, hit);
  }
  return { ...hit, layout };
}

/** Effective formatting of a run (rPrXml) inside paragraph `rich`. */
export function resolveRun(rich: RichParagraph | undefined, rPrXml: string): RunFmt {
  const layout = layoutOf(rich);
  if (!layout || !rich) return parseRun(rPrXml, {});
  const key = (layout.tableStyleOf.get(rich) || '') + '\u0000' + (rich.pPrXml || '') + '\u0000' + rPrXml;
  let hit = layout.runCache.get(key);
  if (!hit) {
    const base = resolvePara(rich).run;
    const rStyle = attrs(rPrXml, 'rStyle')?.val;
    const charChain = rStyle ? styleChain(layout, rStyle).map(s => parseRun(s.rPr, layout.theme)) : [];
    hit = mergeDefined<RunFmt>(base, ...charChain, parseRun(rPrXml, layout.theme));
    layout.runCache.set(key, hit);
  }
  return hit;
}

// ---------- numbering ----------

function lvlDef(layout: DocLayout, numId: string, ilvl: number): LvlDef | undefined {
  const n = layout.nums.get(numId);
  if (!n) return undefined;
  return n.overrides.get(ilvl)?.lvl || layout.abstracts.get(n.abstractId)?.get(ilvl);
}

function toRoman(n: number): string {
  if (n <= 0 || n >= 4000) return String(n);
  const t: [number, string][] = [[1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'],
    [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']];
  let out = '';
  for (const [v, s] of t) while (n >= v) { out += s; n -= v; }
  return out;
}

function toLetter(n: number): string {
  if (n <= 0) return String(n);
  const ch = String.fromCharCode(97 + ((n - 1) % 26));
  return ch.repeat(Math.floor((n - 1) / 26) + 1);
}

function formatNum(n: number, fmt: string): string {
  switch (fmt) {
    case 'lowerLetter': return toLetter(n);
    case 'upperLetter': return toLetter(n).toUpperCase();
    case 'lowerRoman': return toRoman(n);
    case 'upperRoman': return toRoman(n).toUpperCase();
    case 'decimalZero': return n < 10 ? '0' + n : String(n);
    case 'ordinal': {
      const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th';
      return n + s;
    }
    case 'none': return '';
    default: return String(n);
  }
}

// Symbol / Wingdings private-use bullets → the characters they draw.
const BULLETS: Record<string, string> = {
  '': '•', '': '▪', '': '➢', '': '✓',
  '': '❖', '': '□', '': '■', '': '➔',
};

function nextLabel(layout: DocLayout, p: RichParagraph, counters: Map<string, number[]>, seenNum: Set<string>): NumLabel | undefined {
  const { para } = resolvePara(p);
  const numId = para.numId;
  if (!numId || numId === '0') return undefined;
  const n = layout.nums.get(numId);
  if (!n) return undefined;
  const ilvl = Math.min(Math.max(para.ilvl ?? 0, 0), 8);
  const lvls = layout.abstracts.get(n.abstractId);
  const lvlAt = (l: number) => n.overrides.get(l)?.lvl || lvls?.get(l);
  const lvl = lvlAt(ilvl);
  if (!lvl) return undefined;

  let c = counters.get(n.abstractId);
  if (!c) { c = []; counters.set(n.abstractId, c); }
  // A num with startOverride restarts its levels the first time it is used.
  if (!seenNum.has(numId)) {
    seenNum.add(numId);
    for (const [l, o] of n.overrides) if (o.start !== undefined) c[l] = o.start - 1;
  }
  c[ilvl] = (c[ilvl] ?? (lvl.start - 1)) + 1;
  for (let l = ilvl + 1; l < 9; l++) {
    const d = lvlAt(l);
    // lvlRestart="0" means never restart.
    if (d && d.restart === 0) continue;
    c[l] = undefined as any;
  }

  let text: string;
  if (lvl.numFmt === 'bullet') {
    text = Array.from(lvl.lvlText).map(ch => BULLETS[ch] || ch).join('');
  } else {
    text = lvl.lvlText.replace(/%([1-9])/g, (_m, d) => {
      const l = Number(d) - 1;
      const dl = lvlAt(l);
      const v = c![l] ?? (dl ? dl.start : 1);
      return formatNum(v, lvl.isLgl ? 'decimal' : (dl?.numFmt || 'decimal'));
    });
  }
  // The number takes the paragraph mark's formatting plus the level's own rPr.
  const markRPr = (p.pPrXml || '').match(/<w:rPr\b[\s\S]*?<\/w:rPr>/)?.[0] || '';
  const run = mergeDefined<RunFmt>(resolveRun(p, markRPr), parseRun(lvl.rPr, layout.theme));
  // Word does not underline/strike a number just because the paragraph mark is.
  return { text, suff: lvl.suff === 'space' ? ' ' : lvl.suff === 'nothing' ? '' : '\t', run };
}

export function labelOf(rich: RichParagraph | undefined): NumLabel | undefined {
  const layout = layoutOf(rich);
  return layout && rich ? layout.labels.get(rich) : undefined;
}

// ---------- tables ----------

export function resolveTable(row: RichParagraph): TableFmt {
  const layout = layoutOf(row);
  const xml = row.tablePropsXml || '';
  const tblPr = block(xml, 'tblPr');
  const gridTw = Array.from(xml.matchAll(/<w:gridCol\b[^>]*w:w="(\d+)"/g)).map(m => Number(m[1]));
  const styleId = attrs(tblPr, 'tblStyle')?.val || layout?.defaultTableStyle;
  const chain = layout && styleId ? styleChain(layout, styleId).map(s => s.tblPr) : [];
  const sides = ['top', 'left', 'bottom', 'right', 'insideH', 'insideV'] as const;
  const borders = mergeDefined<TableFmt['borders']>(...chain.map(t => parseBorders(t, 'tblBorders', [...sides])),
    parseBorders(tblPr, 'tblBorders', [...sides]));
  const cellMar = { left: 108, right: 108, top: 0, bottom: 0 };
  for (const t of [...chain, tblPr]) {
    const mar = block(t, 'tblCellMar');
    if (!mar) continue;
    for (const s of ['left', 'right', 'top', 'bottom'] as const) {
      const alt = s === 'left' ? 'start' : s === 'right' ? 'end' : '';
      const w = num((attrs(mar, s) || (alt ? attrs(mar, alt) : null))?.w);
      if (w !== undefined) cellMar[s] = w;
    }
  }
  let indTw = 0;
  for (const t of [...chain, tblPr]) {
    const w = num(attrs(t, 'tblInd')?.w);
    if (w !== undefined) indTw = w;
  }
  const jc = attrs(tblPr, 'jc')?.val;
  return { gridTw, indTw, jc, cellMar, borders, styleId };
}

export function resolveCell(tcPrXml: string): CellFmt {
  const vm = attrs(tcPrXml, 'vMerge');
  return {
    widthTw: attrs(tcPrXml, 'tcW')?.type === 'dxa' ? num(attrs(tcPrXml, 'tcW')?.w) : undefined,
    span: num(attrs(tcPrXml, 'gridSpan')?.val) || 1,
    vMerge: vm ? (vm.val === 'restart' ? 'restart' : 'continue') : undefined,
    vAlign: attrs(tcPrXml, 'vAlign')?.val,
    shd: shdFill(tcPrXml),
    borders: parseBorders(tcPrXml, 'tcBorders', ['top', 'left', 'bottom', 'right'] as const),
  };
}

export function rowHeightTw(trPrXml: string): number | undefined {
  return num(attrs(trPrXml, 'trHeight')?.val);
}

/** Whether a table row is the first / last row of its table (for outer borders). */
export function rowPosition(row: RichParagraph): { first: boolean; last: boolean } {
  return layoutOf(row)?.rowPos.get(row) || { first: true, last: true };
}

/** Page geometry of the document a paragraph came from, for the on-screen page. */
export function pageOf(rich: RichParagraph | undefined): { widthTw: number; marginLTw: number; marginRTw: number; tabTw: number } | undefined {
  const l = layoutOf(rich);
  return l ? { widthTw: l.pageWidthTw, marginLTw: l.marginLTw, marginRTw: l.marginRTw, tabTw: l.defaultTabTw } : undefined;
}
